<template>
  <section>
    <div class="container tm-product-container product__breadcrumbs">
      <Breadcrumbs :breadcrumbs="breadcrumbs" />
    </div>

    <div
      v-if="currentProduct"
      class="container tm-product-page"
    >
      <div
        id="product_20158"
        data-id="20158"
        class="product mobile-container"
      >
        <div class="product__mobile-title">{{ currentProduct?.name }}</div>
        <div class="product__left">
          <SliderImages
            :images="currentProduct?.imageIds"
            :product-id="String(currentProduct?.id)"
            :is-product-card="true"
            @open-popup-gallery="openPopupGallery"
          />
        </div>

        <div class="product__right">
          <div class="product__name">
            {{ currentProduct?.name }}
          </div>
          <div class="product__line">
            <AddToCardBtn
              :product="currentProduct"
              :selected-options-list="selectedOptionsList"

              @check-options-errors="checkSelectedOptionsError"
            />
            <div class="product__prices">
              <Price
                :old-price="currentProduct.oldPrice"
                :price="setTotalPrice"
              />
            </div>
          </div>
          <div
            v-if="Boolean(currentProduct.hasOptions && currentProduct.options)"
            class="product-card__options"
          >
            <div
              v-if="isSelectedOptionsHasError === true"
              class="product-option__error-text"
            >
              Выберите конфигурацию:
            </div>

            <ProductOptions
              :product-options="currentProduct.options || []"
              @updateOptionsList="onUpdateOptionsList"
            />
          </div>
          <div class="product__specifications">
            <dl
              v-for="(specification, index) in specifications"
              :class="{'--full': specification.isFull}"
              :key="`product-specification-${index}`"
            >
              <dt v-html="specification.label"/>
              <dd v-html="specification.value"/>
            </dl>
          </div>
        </div>
      </div>
      <client-only>
        <ProductsWithPay
          :products-recently="state.productsRecently"
          :products-related="state.productsRelated"
          :products-same="otherProductsCollection"
        />
      </client-only>
    </div>
  </section>
</template>

<script setup lang="ts">
import useSitisProduct from '#sitis/composables/useSitisProduct';
import SliderImages from '~/components/products/slider-images/SliderImages.vue';
import AddToCardBtn from '~/components/UI/AddToCartPoduct.vue';
import { productStore as useProductStore } from '#sitis/stores/modules/product-store';
import Price from '~/components/collection/Price.vue';
import ProductOptions from '~/components/products/ProductOptions.vue';
import type { IProductOptionsVariant } from '#sitis/internal/controllers/products/models/ProductOptions';
import useEcommerce from '~/composables/useEcommerceYandex';
import {type IProduct} from "#sitis/internal/controllers/products/models/Product";
import { Product } from '#sitis/internal/controllers/products/models/Product';
import type {ComputedRef} from "vue";
import {useNuxtApp} from "#app";

export interface IExtendedProductOptionsVariant extends IProductOptionsVariant {
	productOptionId: number;
}

const { addEcommerce } = useEcommerce();
const Breadcrumbs = defineAsyncComponent(() => import('~/components/UI/Breadcrumbs.vue'));

const productStore = useProductStore();
const productImage = useImage();
const {$api} = useNuxtApp();

const {
  state,

  getProduct,
  getProductsRelated,
  getProductsRecently
} = useSitisProduct();

const {
  data: currentProduct,
  pending: isLoadingProduct
} = await useAsyncData('getCurrentProducts', () => getProduct(
  [
    'options',
    'productSetParents',
    'options.variants'
  ]
));
if (!currentProduct.value) {
  throw createError({ message: 'Page not found', statusCode: 404, fatal: true });
}

useSitisCMSPage().setSEOTitles({
  seoTitle: currentProduct.value?.seo?.title,
  seoDescription: currentProduct.value?.seo?.description,
  seoKeywords: currentProduct.value?.seo?.keywords
});
const breadcrumbs = computed(() => productStore.breadcrumbs);

const specifications: ComputedRef<{label: string, value: string, isFull: boolean}[]> = computed(() => {
  const hiddenSlugs = ['discount-label-date', 'discount-label-auto', 'discount-label', 'lejbl-dlya-skidki-polnoe-opisanie'];

  let _characteristics: any[] = [];
  (currentProduct.value?.characteristics || []).map((_item) => {
    _characteristics.push(...(_item?.characteristics || []));
  });
  _characteristics = _characteristics.filter((t: any) => {
    if (hiddenSlugs.includes(t.slug)) {
      return false
    }
    if (t.viewTemplate === 'for_custom_sections') {
      return false
    }
    return Boolean(t.showInProduct)
  });
  _characteristics = _characteristics.sort((a, b) => {
    if (a.sort < b.sort) {
      return 1
    }
    if (a.sort > b.sort) {
      return -1
    }
    return 0
  });

  return _characteristics.map((t: any) => {
    return {
      label: t?.nameForProduct || t?.name || '',
      value: (t?.values || []).map((_val: any) => `<span class="nowrap">${_val?.value}</span>`).join(', '),
      isFull: Boolean(t.viewTemplate === 'full_width')
    }
  });
});

const { openGallery } = useGallery();
const openPopupGallery = () => {
  const _product: any = state.product;
  openGallery({
    imagesIds: _product?.imageIds || currentProduct?.value?.imageIds || [],
    title: _product?.name || '',
    indexActiveSlide: 1
  });
};

const selectedOptionsList = ref<IExtendedProductOptionsVariant [] >([]);
const isSelectedOptionsHasError = ref<boolean | undefined>();

const onUpdateOptionsList = (options:IExtendedProductOptionsVariant []) => {
  selectedOptionsList.value = options;

  if (currentProduct.value?.options && currentProduct.value?.options.length === selectedOptionsList.value?.length) {
    checkSelectedOptionsError(false);
  }
};
const checkSelectedOptionsError = (isHasErr:boolean) => {
  isSelectedOptionsHasError.value = isHasErr;
};

const setTotalPrice = computed(() => {
  let extraPrice = 0;
  if (selectedOptionsList.value) {
    extraPrice = selectedOptionsList.value.reduce((accum, curVal) => accum + curVal.price, 0);
  }

  return (currentProduct.value?.price || 0) + extraPrice;
});
function availabilityDate () {
  const todayDate = new Date();
  const newDay = todayDate.setDate(todayDate.getDate() + 1);
  return new Date(newDay).toISOString();
}
useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: currentProduct.value?.name || 'name',
  description: currentProduct.value?.description || '',
  image: productImage(String(currentProduct.value?.imageId)),
  offers: {
    '@type': 'Offer',
    price: currentProduct.value?.price,
    priceCurrency: 'RUB',
    sku: currentProduct.value?.sku || '',
    availability: 'https://schema.org/PreOrder',
    priceValidUntil: availabilityDate()
  },
  brand: {
    '@type': 'Brand',
    name: currentProduct.value?.brand?.name || ''
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    bestRating: 5,
    worstRating: 4,
    ratingValue: 4.89,
    ratingCount: 843
  }
}));

const otherProductsCollection: Ref<IProduct[]> = ref([]);
const getOtherProductsCollection = async () => {

  // if (isLoadingProduct.value) {
  //   return null
  // }

  const _product: any = currentProduct.value;
  const slugParent = _product?.productSetParents?.[0]?.slug || '';
  if (!slugParent) {
    return []
  }

  let _products: IProduct[] = await $api.agent.get(`/products/${ slugParent }/product-set-children?per-page=5`).then((res) => {
    return [...(res._data || [])].map((item: any) => {
      return new Product(item)
    })
  }).catch(() => {
    return []
  })
  _products = _products.filter((_t: any) => Boolean(_t?.id !== _product?.id))
  otherProductsCollection.value = _products;
};
watch(isLoadingProduct, async () => {
  // await getOtherProductsCollection()
})

onMounted(async () => {
  await getProductsRelated();
  await getProductsRecently();
  await getOtherProductsCollection();
  addEcommerce('detail', currentProduct.value);
});
</script>

<style scoped lang="scss">
.product__mobile-title {
  display: none;
  font-weight: 700;
  font-size: 18px;
  line-height: 23px;
  color: #000;
  margin-bottom: 20px;
  text-decoration: none;
}

.tm-product-page {
  padding-bottom: 92px;
}

.product-page__controls .btn{
  background-color: transparent;
}

.product {
  display: -webkit-box;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  border-bottom: 1px solid #d0d0d0;
  padding-bottom: 30px;
  margin-bottom: 30px;
}

.product__left, .product__right {
  width: calc(50% - 12px);
}

.product__left {
  margin-top: 20px;
}

.product__name {
  font-weight: 700;
  font-size: 32px;
  line-height: 41px;
  color: #000;
  width: 94%;
  margin-bottom: 52px;
  display: block;
  text-decoration: none;
}

.product__line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 67px;
}

.product__description {
  width: 74%;
  font-size: 18px;
  line-height: 23px;
  color: #000;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.product__description-text {
  & p {
    margin-bottom: 13px;
    font-size: 18px;
    line-height: 23px;
    color: #000;
  }
}

.product__prices{
  .product-price__root{
    align-items: flex-end;
  }
}

.product-code{
  font-size: 18px;
  line-height: 23px;
  color: #000;
  margin-bottom: 20px;
  font-weight: 700;

  & > .value{
    font-weight: 400;
  }
}

.product-card__options{
  margin-bottom: 30px;
}

.product-option__error-text{
  font-size: 18px;
  margin-top: -20px;
  margin-bottom: 20px;
  font-weight: 700;
  color: red;
}

@media screen and (max-width: 1199px) {
  .product__name {
    font-size: 28px;
    line-height: 37px;
    margin-bottom: 38px;
  }

  .product__currency {
    font-size: 24px;
    line-height: 32px;
  }
}

@media screen and (max-width: 990px) {
  .product {
    margin-bottom: 30px;
    padding-bottom: 20px;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    flex-direction: column;
  }

  .product__currency {
    font-size: 19px;
    line-height: 25px;
  }

  .product__old {
    font-size: 12px;
    line-height: 17px;
    min-width: initial;
    max-width: initial;
  }

  .product__left {
    margin-bottom: 15px;
    margin-top: 0;
  }

  .product__left, .product__right {
    width: 100%;
  }

  .product__name {
    display: none;
  }

  .product__mobile-title {
    display: block;
    margin-bottom: 10px;
  }

  .product__description-text p {
    font-size: 14px;
    line-height: 18px;
  }
}

</style>
<style lang="scss">
.product-page__controls {
  & .btn{
    background-color: transparent;
    color: var(--primary);
    border: 2px solid var(--primary);
    height: 46px;
  }
}
.product__specifications {
  display: flex;
  flex-direction: column;
  width: 80%;
  margin-top: 16px;
  gap: 16px;

  dl {
    width: 100%;
    font-size: 18px;
    line-height: 23px;
    color: #000;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    margin: 0;
  }
  dt {
    padding-right: 10px;
  }
  dt, dd {
    width: 50%;
  }

  dl.--full {
    dt {
      display: none;
    }
    dd {
      width: 100%;
    }
    dd span {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
  }
}

@media screen and (max-width: 990px) {
  .product__specifications {
    dl {
      font-size: 14px;
      line-height: 18px;
    }
  }
}
</style>
